import React, { useEffect, useState, useRef } from 'react';
import { Link, Navigate, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { clearAuthError, resetState } from '@/actions';
import { AuthState } from '@/types';
import TelegramLoginButton from '@/components/TelegramLoginButton';
import StaticLayout from '@/layouts/StaticLayout';
import LoaderIcon from '@/images/icons/Loader';
import ModalAction from '@/components/ModalAction';
import TelegramIcon from '@/images/icons/TelegramIcon';
import EmailConfirmModal from '@/components/EmailConfirmModal';
import useSeamlessLogin from '@/hooks/useSeamlessLogin';
import ErrorModal from '@/components/ErrorModal';
import { getRedirectUrlWithParams } from '@/utils/utils';

const Signup: React.FC = () => {
  const {
    loginAttemptMade,
    isSeamlessLogin,
    preLoggedIn,
    onLogin,
    isLoginFailed,
  } = useSeamlessLogin();

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const redirectedEmail = location.state?.email || '';

  const auth = useSelector((state: { auth: AuthState }) => state.auth);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [telegramModalOpen, setTelegramModalOpen] = useState<boolean>(false);
  const [emailConfirmModalOpen, setEmailConfirmModalOpen] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<string | null>(null);
  const [errorModalButton, setErrorModalButton] = useState<any>(null);
  const [errorModalCallback, setErrorModalCallback] = useState<any>(null);
  const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);

  const [email, setEmail] = useState<string>(redirectedEmail);
  const [lastEmail, setLastEmail] = useState<string>('');
  const [emailConfirmCode, setEmailConfirmCode] = useState<string>('');
  const [requestId, setRequestId] = useState<string | null>(null);

  const requestIdRef = useRef<string | null>(null);
  const callbackRef = useRef(null);

  const [resendTimer, setResendTimer] = useState<number>(0);
  const [canResend, setCanResend] = useState<boolean>(true);
  const [imTerms, setImTerms] = useState<boolean>(false);
  const [isEmailVerifyLoading, setIsEmailVerifyLoading] = useState<boolean>(false);
  const [hasResendBeenClicked, setHasResendBeenClicked] = useState<boolean>(false);

  const redirect = searchParams.get('redirectUrl');

  useEffect(() => {
    if (auth.justLoggedOut) {
      dispatch(resetState());
    }
    return () => {
      if (auth.error) {
        dispatch(clearAuthError());
      }
    }
  }, []);

  useEffect(() => {
    if (auth.user) {
      const redirect = searchParams.get('redirectUrl');

      if (redirect) {
        navigate(redirect, { replace: true });
      }
    }
  }, [auth.user, navigate, searchParams]);

  useEffect(() => {
    if (errorModal) {
      setTelegramModalOpen(false);
      setEmailConfirmModalOpen(false);
      setErrorModalOpen(true);
    }
  }, [errorModal]);

  useEffect(() => {
    if (!errorModalOpen) {
      setErrorModal(null);
      dispatch(clearAuthError());
    }
  }, [errorModalOpen]);

  useEffect(() => {
    requestIdRef.current = requestId;
  }, [requestId]);

  useEffect(() => {
    if (auth?.error) {
      setErrorModal(auth.error);
      if (auth.errorStatus === 409) {
        setErrorModalButton('Login');
        // @ts-ignore
        callbackRef.current = () => {
          dispatch(clearAuthError());
          setTimeout(() => navigate('/login'), 100);
        };
        setErrorModalCallback(() => callbackRef.current);
      }
    }
  }, [auth?.error]);

  useEffect(() => {
    if (email !== lastEmail) {
      setIsEmailVerified(false);
      setRequestId(null);
    }
  }, [email]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  useEffect(() => {
    if (emailConfirmCode.length === 6) {
      onEmailVerify();
    }
  }, [emailConfirmCode.length]);

  useEffect(() => {
    if (!emailConfirmModalOpen) {
      setErrorCode(null);
    }
  }, [emailConfirmModalOpen]);

  const sendCodeEmail = async () => {
    try {
      setIsLoading(true);
      setErrorModal(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.redirectUrl) {
          navigate(`/login?${searchParams}`, { state: { email } });
        } else if (!data.isVerified && data.requestId) {
          setRequestId(data.requestId);
          setErrorModal(null);
          setEmailConfirmModalOpen(true);
          setResendTimer(60);
          setCanResend(false);
        }
      } else {
        setErrorModal(data.message);
      }
    } catch (error) {
      console.error('Error creating recovery request:', error);
      setErrorModal('An error occurred, please try again later.');
    } finally {
      setIsLoading(false);
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLastEmail(email);
    if (!imTerms || isLoading) return;

    if (isEmailVerified) {
      setTelegramModalOpen(true);
    } else if (!requestId) {
      sendCodeEmail();
    } else if (requestId) {
      setEmailConfirmModalOpen(true);
    }
  };

  const onEmailResend = () => {
    setHasResendBeenClicked(true);

    if (canResend) {
      setResendTimer(60);
      setCanResend(false);
      sendCodeEmail();
    }
  };

  const onEmailVerify = async () => {
    if (isEmailVerifyLoading) return;

    try {
      setErrorCode(null);
      setIsEmailVerifyLoading(true);
      setErrorModal(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requestId,
          verificationCode: emailConfirmCode,
        }),
      });

      // const response = await new Promise((resolve) => {
      // 	setTimeout(() => {
      // 		resolve({
      // 			ok: true,
      // 			json: () => Promise.resolve({ requestId: 'mock-request-id' }),
      // 			// ok: false,
      // 			// json: () => Promise.resolve({ message: "Something went wrong" }),
      // 		});
      // 	}, 5000);
      // });
      // // @ts-ignore
      const data = await response.json();

      // @ts-ignore
      if (response.ok) {
        setErrorModal(null);
        setEmailConfirmModalOpen(false);
        setTelegramModalOpen(true);
        setIsEmailVerified(true);
      } else {
        setErrorCode(data.message);
      }
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setErrorModal(error?.response?.message || 'An error occurred, please try again later.');
    } finally {
      setIsEmailVerifyLoading(false);
    }
  };

  if (preLoggedIn) {
    const redirectUrl = getRedirectUrlWithParams(searchParams);
    return <Navigate to={redirectUrl} replace />;
  }

  if (isSeamlessLogin && !loginAttemptMade) {
    return null;
  }

  const footerLinks = [
    {
      title: 'Terms',
      link: 'https://www.invitemember.com/terms',
    },
    {
      title: 'Privacy',
      link: 'https://www.invitemember.com/privacy'
    },
    {
      title: 'Support',
      link: 'https://t.me/InviteMemberSupportBot'
    }
  ];

  return (
    <StaticLayout logoPosition={"left"} size={'xl'} layoutCenter layoutFixed>
      <div className="basis-0 grow shrink my-10 md:my-0">
        {(loginAttemptMade && !preLoggedIn && !isLoginFailed) ? (
          <div className="flex items-center justify-center py-1">
            <svg
              className="animate-rotation w-10 h-10"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                d="M12 2C11.4467 2 10.9039 2.04494 10.375 2.13136"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                d="M2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2"
                stroke="#8A9698"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>
        ) : (
          <>
            <h1 className="text-3xl md:text-[5.5rem] font-semibold text-brand-green mb-5 md:mb-14 font-founders tracking-normal leading-none text-center mx-auto">
              Join InviteMember
            </h1>

            <div className="max-w-[422px] mx-auto">
              <form onSubmit={onSubmit}>
                <input
                  type="email"
                  className="h-10 md:h-16 w-full py-2 md:py-[18px] px-4 md:px-5 text-base bg-[#FEFEF9] border border-[#DBDBDB] rounded-[8px] md:rounded-[12px] placeholder:text-[#A5A5A5] focus:border-[#3CBBCE] transition"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <label className="flex items-center mt-4 md:mt-8 mb-4 md:mb-0">
                  <input type="checkbox"
                         className="form-checkbox w-5 md:w-6 h-5 md:h-6"
                         required
                         checked={imTerms}
                         onChange={() => setImTerms(!imTerms)} />
                  <span className="text-[11px]/[1.2] md:text-sm text-[#A5A5A5] text-left ml-1.5 md:ml-3">
                    I accept InviteMember <a className="text-[#3CBBCE] underline hover:no-underline" href="https://www.invitemember.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a className="text-[#3CBBCE] underline hover:no-underline" href="https://www.invitemember.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  </span>
                </label>
                <button
                  className={classNames(`btn-primary w-full h-10 md:h-14 mt-4 md:mt-6 mb-2`)}
                  type="submit"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center py-1 pointer-events-none">
                      <LoaderIcon className="animate-rotation w-6 h-6 text-white" />
                    </div>
                  ) : (
                    <span className="font-founders tracking-[0.02em] text-2xl md:text-3xl/[0.8] font-semibold translate-y-[-1px] md:translate-y-[-3px]">
                      Continue
                    </span>
                  )}
                </button>
              </form>
              <div className="mt-20 text-center text-[13px] md:text-base">
                <span className="text-[#A5A5A5]">Already using InviteMember?</span>
                &nbsp;
                {redirect ? (
                  <Link to={`/login?redirectUrl=${encodeURIComponent(redirect)}`} className="text-[#3CBBCE]">Log in</Link>
                ) : (
                  <Link to="/login" className="text-[#3CBBCE]">Log in</Link>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <ModalAction
        id="signup-telegram-modal"
        modalOpen={telegramModalOpen}
        setModalOpen={setTelegramModalOpen}
        size={undefined}
      >
        <TelegramIcon className="w-14 h-14 rounded-full mb-6" />
        <h2 className="h2 mb-4 leading-[0.9]">
          Almost done!
        </h2>
        <div className="mb-8">To continue, please log in using your Telegram account.</div>
        {auth?.isLoggingIn ? (
          <div className="w-10 h-10 flex items-center justify-center mx-auto overflow-hidden">
            <svg className="w-10 h-10 animate-rotation"
                 width="48"
                 height="48"
                 viewBox="0 0 48 48"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M44 24C44 35.0457 35.0457 44 24 44C12.9543 44 4 35.0457 4 24C4 12.9543 12.9543 4 24 4"
                    stroke="#EAE6E0"
                    strokeWidth="8"
                    strokeLinecap="round" />
              <path d="M24 4C22.8934 4 21.8077 4.08988 20.75 4.26273"
                    stroke="#EAE6E0"
                    strokeWidth="8"
                    strokeLinecap="round" />
              <path d="M4 24C4 35.0457 12.9543 44 24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4"
                    stroke="#0D4E58"
                    strokeWidth="8"
                    strokeLinecap="round" />
            </svg>
          </div>
        ) : (
          <TelegramLoginButton
            dataOnauth={(data: any) => onLogin(data, false, requestIdRef.current)}
            botName={process.env.REACT_APP_BOT_USERNAME}
          />
        )}
      </ModalAction>
      <EmailConfirmModal
        modalOpen={emailConfirmModalOpen}
        setModalOpen={setEmailConfirmModalOpen}
        email={email}
        emailConfirmCode={emailConfirmCode}
        setEmailConfirmCode={setEmailConfirmCode}
        resendTimer={resendTimer}
        onResend={onEmailResend}
        onVerify={onEmailVerify}
        hasResendBeenClicked={hasResendBeenClicked}
        isLoading={isEmailVerifyLoading || auth?.isLoggingIn}
        error={errorCode}
      />
      <ErrorModal
        modalOpen={errorModalOpen}
        setModalOpen={setErrorModalOpen}
        error={errorModal}
        buttonText={errorModalButton}
        onClearModal={errorModalCallback}
      />

      <div className="flex items-center justify-center mt-auto md:mt-[60px] mb-3 flex-row">
        {footerLinks.map((item) => (
          <a key={item.link}
             href={item.link}
             className="text-[11px]/[1.4] md:text-[13px] text-brand-green relative before:first:hidden before:content-[''] before:mr-2 before:ml-0 before:block before:w-[3px] md:before:w-1 before:h-[3px] md:before:h-1 before:bg-brand-green before:rounded-full before:absolute before:z-[1] before:left-0 before:top-1/2 before:-translate-y-1/2 before:-translate-x-1/2 before:opacity-50 before:pointer-events-none"
          >
            <span className="px-[14px] py-2 opacity-50 hover:opacity-100 transition">{item.title}</span>
          </a>
        ))}
      </div>
    </StaticLayout>
  );
};

export default Signup;
